import {Text, Box, useSlotRecipe} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import React, {useMemo} from 'react'
import headingSlotRecipe from './index.recipe'
import {hasValidString} from '../../../utils/utils'

/**
 * Renders a heading with optional subline and customizable alignment.
 * Intended for use within the Leap components library.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {1|2|3|4|5|6} props.headingLevel - HTML heading level (1–6) used to determine the `<h*>` tag.
 * @param {string} props.headline1 - Main heading text first part.
 * @param {string} props.headline2 - Main heading text second part.
 * @param {string} [props.subline] - Optional secondary text displayed below the headline.
 * @param {Object | string} [props.alignment='left'] - Horizontal alignment of the heading content, could be used a chakra breakpoint object
 * @param {('default'|'small'|'hidden')} [props.variant='default'] - 'default'|'small' variants changes textStyles, 'hidden' - collapses element space
 * @returns {JSX.Element} A heading element with an optional subline.
 */

const Heading = ({
    headingLevel,
    headline1,
    headline2,
    separateLines = false,
    dualTone = false,
    subline,
    alignment = 'left',
    variant = 'default',
    css
}) => {
    const recipe = useSlotRecipe({recipe: headingSlotRecipe})
    const styles = useMemo(
        () =>
            recipe({
                dualTone,
                separateLines,
                variant
            }),
        [recipe, dualTone, separateLines, variant]
    )

    if (variant === 'hidden') return null

    const validHeadline1 = hasValidString(headline1)
    const validHeadline2 = hasValidString(headline2)

    if (!validHeadline1 && !validHeadline2 && !hasValidString(subline)) return null

    return (
        <Box as={`h${headingLevel}`} css={[styles.root, css]} textAlign={alignment}>
            {subline && (
                <Text as="span" css={styles.subline}>
                    {subline}
                </Text>
            )}
            {validHeadline1 && (
                <Box as="span" css={styles.headline}>
                    <Text as="span" css={styles.headline1}>
                        {headline1}
                        {validHeadline2 && !separateLines && '\u00A0'}
                    </Text>
                    {validHeadline2 && (
                        <Text as="span" css={styles.headline2}>
                            {headline2}
                        </Text>
                    )}
                </Box>
            )}
        </Box>
    )
}

Heading.propTypes = {
    headingLevel: PropTypes.number,
    colorScheme: PropTypes.string,
    subline: PropTypes.string,
    alignment: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    headline1: PropTypes.string,
    headline2: PropTypes.string,
    separateLines: PropTypes.bool,
    dualTone: PropTypes.bool,
    variant: PropTypes.string,
    css: PropTypes.object
}

export default Heading
